import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Check, AlertCircle, CheckCircle } from 'lucide-react';
import QRCode from 'react-qr-code';

const API_BASE = "/api";

function TelegramSettingsModal({ isOpen, onClose, darkMode = false }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const redColor = '#FD0E0B';
  const greenColor = '#95DE1A';

  useEffect(() => {
    if (isOpen) fetchChats();
  }, [isOpen]);

  const fetchChats = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/telegram/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setChats(data);
      else setError(data.error || 'Failed to load list');
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const generateLink = async () => {
    setGenerating(true);
    setError('');
    setSuccess('');
    setGeneratedLink(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/telegram/generate-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setGeneratedLink(data);
        setSuccess('Connection code created. Scan the QR code or use the link.');
      } else {
        setError(data.error || 'Failed to create code');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setGenerating(false);
    }
  };

  const deleteChat = async (chatId) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/telegram/chats/${chatId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setChats(chats.filter(c => c.id !== chatId));
        setSuccess('Connection deleted.');
      } else {
        const data = await res.json();
        setError(data.error || 'Delete failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const updateName = async (chatId) => {
    if (!editName.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/telegram/chats/${chatId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: editName })
      });
      if (res.ok) {
        setChats(chats.map(c => c.id === chatId ? { ...c, name: editName } : c));
        setEditingId(null);
        setSuccess('Name updated.');
      } else {
        const data = await res.json();
        setError(data.error || 'Update failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Sabit başlık */}
        <div className={`flex items-center justify-between p-5 border-b flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: redColor }}>Telegram Notifications</h2>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Connected accounts (max 5)</p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Kaydırılabilir içerik */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center space-x-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-sm">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl text-sm">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span className="text-green-700 dark:text-green-300">{success}</span>
            </div>
          )}

          {/* Liste */}
          <div className="space-y-2">
            {loading && chats.length === 0 ? (
              <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
            ) : chats.length === 0 ? (
              <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No connected accounts yet.</p>
            ) : (
              chats.map(chat => (
                <div key={chat.id} className={`flex items-center justify-between p-3 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  {editingId === chat.id ? (
                    <div className="flex-1 flex items-center space-x-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className={`flex-1 px-3 py-1.5 border rounded-lg text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`}
                        placeholder="Name"
                        autoFocus
                      />
                      <button
                        onClick={() => updateName(chat.id)}
                        className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{chat.name || 'Unnamed'}</p>
                        <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {chat.chatId}</p>
                      </div>
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => {
                            setEditingId(chat.id);
                            setEditName(chat.name || '');
                          }}
                          className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"
                          title="Edit name"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteChat(chat.id)}
                          className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Yeni bağlantı butonu */}
          {chats.length < 5 && (
            <div className="space-y-4 pt-2">
              <button
                onClick={generateLink}
                disabled={generating}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-white font-medium rounded-xl shadow-md transition-all hover:shadow-lg disabled:opacity-50 text-sm"
                style={{ background: `linear-gradient(135deg, ${greenColor} 0%, #95DE1A 50%, #A8F02B 100%)` }}
              >
                <Plus className="h-4 w-4" />
                <span>{generating ? 'Creating...' : 'Create New Connection'}</span>
              </button>

              {generatedLink && (
                <div className={`rounded-xl p-4 space-y-3 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm font-medium text-center ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Scan QR code with your phone:</p>
                  
                  <div className="flex justify-center">
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <QRCode value={generatedLink.link} size={160} bgColor={darkMode ? '#1f2937' : '#ffffff'} fgColor={darkMode ? '#ffffff' : '#000000'} />
                    </div>
                  </div>

                  <div className={`text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>or</div>

                  <div className={`rounded-lg border p-3 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <code className={`text-xs font-mono break-all ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>{generatedLink.code}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generatedLink.code);
                          setSuccess('Code copied!');
                        }}
                        className={`ml-2 px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                      >
                        Copy
                      </button>
                    </div>
                    <div className={`text-xs break-all ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <a href={generatedLink.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">
                        {generatedLink.link}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {chats.length >= 5 && (
            <p className="text-center text-xs text-red-600 dark:text-red-400 font-medium">Maximum 5 connections reached.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default TelegramSettingsModal;